import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStandard, ButtonStandardType } from 'record-it-components/button/ButtonStandard/ButtonStandard';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { TextInput, TextInputType } from 'record-it-components/input/TextInput/TextInput';
import { MessageBoxType } from 'record-it-components/misc/MessageBox/MessageBox';
import { MessageListBox } from 'record-it-components/misc/MessageListBox/MessageListBox';
import { MessageListBoxListItem } from 'record-it-components/misc/MessageListBoxListItem/MessageListBoxListItem';
import { ConfirmModal, ConfirmModalProps } from 'record-it-components/modal/ConfirmModal/ConfirmModal';
import { ConfirmModalBody } from 'record-it-components/modal/ConfirmModalBody/ConfirmModalBody';
import { ConfirmModalButtonFooter } from 'record-it-components/modal/ConfirmModalButtonFooter/ConfirmModalButtonFooter';
import { ConfirmModalHeader } from 'record-it-components/modal/ConfirmModalHeader/ConfirmModalHeader';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { ApiAdapter } from '../../../classes/ApiAdapter/ApiAdapter';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import styles from './CreateUserDialog.module.css';
export type CreateUserDialogProps = PropsWithoutChildren<ConfirmModalProps> & {
  onCancel: () => void;
  onUserCreated: () => void;
};
export const CreateUserDialog: FunctionComponent<CreateUserDialogProps> = React.memo(props => {
  const $ = _c(68);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let onCancel;
  let onUserCreated;
  let remainingProps;
  if ($[2] !== t0) {
    ({
      onCancel,
      onUserCreated,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = onCancel;
    $[4] = onUserCreated;
    $[5] = remainingProps;
  } else {
    onCancel = $[3];
    onUserCreated = $[4];
    remainingProps = $[5];
  }
  const [failedToCreateUser, setFailedToCreateUser] = useState(false);
  const [noEmailProvided, setNoEmailProvided] = useState(false);
  const [noPasswordProvided, setNoPasswordProvided] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const {
    t
  } = useTranslation();
  const userRepository = useEntityRepository(EntityName.User);
  let t1;
  if ($[6] !== email || $[7] !== password) {
    t1 = () => {
      let valid = true;
      if (!email.trim()) {
        setNoEmailProvided(true);
        valid = false;
      } else {
        setNoEmailProvided(false);
      }
      if (!password) {
        setNoPasswordProvided(true);
        valid = false;
      } else {
        setNoPasswordProvided(false);
      }
      return valid;
    };
    $[6] = email;
    $[7] = password;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const validate = t1;
  let t2;
  if ($[9] !== email || $[10] !== onUserCreated || $[11] !== password || $[12] !== userRepository || $[13] !== validate) {
    t2 = () => {
      if (!validate()) {
        return;
      }
      setFailedToCreateUser(false);
      setNoEmailProvided(false);
      setNoPasswordProvided(false);
      ApiAdapter.createUser({
        email,
        password
      }).then(response => {
        if (response.success) {
          userRepository.addExternalEntityLocally(response.user);
          onUserCreated();
        } else {
          setFailedToCreateUser(true);
        }
      }).catch(e => {
        console.error(e);
        setFailedToCreateUser(true);
      });
    };
    $[9] = email;
    $[10] = onUserCreated;
    $[11] = password;
    $[12] = userRepository;
    $[13] = validate;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const handleCreateClick = useEventCallback(t2);
  let t3;
  if ($[15] !== failedToCreateUser || $[16] !== t) {
    t3 = [...(failedToCreateUser ? [t("components.user.CreateUserDialog.failedToCreateUser")] : [])];
    $[15] = failedToCreateUser;
    $[16] = t;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const errorMessages = t3;
  let t4;
  if ($[18] !== noEmailProvided || $[19] !== t) {
    t4 = noEmailProvided ? [t("components.user.CreateUserDialog.noEmailProvided")] : [];
    $[18] = noEmailProvided;
    $[19] = t;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== noPasswordProvided || $[22] !== t) {
    t5 = noPasswordProvided ? [t("components.user.CreateUserDialog.noPasswordProvided")] : [];
    $[21] = noPasswordProvided;
    $[22] = t;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== t4 || $[25] !== t5) {
    t6 = [...t4, ...t5];
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const warningMessages = t6;
  let t7;
  if ($[27] !== t) {
    t7 = t("components.user.CreateUserDialog.title");
    $[27] = t;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== t7) {
    t8 = <ConfirmModalHeader>{t7}</ConfirmModalHeader>;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== errorMessages) {
    t9 = errorMessages.length ? <MessageListBox type={MessageBoxType.Error} data-testid="CreateUserDialog.ErrorBox" className={styles.messageListBox}>{errorMessages.map(_temp)}</MessageListBox> : null;
    $[31] = errorMessages;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== warningMessages) {
    t10 = warningMessages.length ? <MessageListBox type={MessageBoxType.Warning} data-testid="CreateUserDialog.WarningBox" className={styles.messageListBox}>{warningMessages.map(_temp2)}</MessageListBox> : null;
    $[33] = warningMessages;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t) {
    t11 = t("entities.User.email");
    $[35] = t;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== email || $[38] !== t11) {
    t12 = <FormGroup><TextInput label={t11} type={TextInputType.Email} value={email} onInput={setEmail} data-testid="CreateUserDialog.EmailInput" /></FormGroup>;
    $[37] = email;
    $[38] = t11;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t) {
    t13 = t("entities.User.password");
    $[40] = t;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== password || $[43] !== t13) {
    t14 = <FormGroup><TextInput label={t13} type={TextInputType.Password} value={password} onInput={setPassword} data-testid="CreateUserDialog.PasswordInput" /></FormGroup>;
    $[42] = password;
    $[43] = t13;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t10 || $[46] !== t12 || $[47] !== t14 || $[48] !== t9) {
    t15 = <ConfirmModalBody>{t9}{t10}{t12}{t14}</ConfirmModalBody>;
    $[45] = t10;
    $[46] = t12;
    $[47] = t14;
    $[48] = t9;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== t) {
    t16 = t("components.user.CreateUserDialog.create");
    $[50] = t;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== handleCreateClick || $[53] !== t16) {
    t17 = <ButtonStandard type={ButtonStandardType.Primary} onClick={handleCreateClick} data-testid="CreateUserDialog.CreateButton">{t16}</ButtonStandard>;
    $[52] = handleCreateClick;
    $[53] = t16;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] !== t) {
    t18 = t("components.user.CreateUserDialog.cancel");
    $[55] = t;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  let t19;
  if ($[57] !== onCancel || $[58] !== t18) {
    t19 = <ButtonStandard type={ButtonStandardType.Secondary} onClick={onCancel} data-testid="CreateUserDialog.CancelButton">{t18}</ButtonStandard>;
    $[57] = onCancel;
    $[58] = t18;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== t17 || $[61] !== t19) {
    t20 = <ConfirmModalButtonFooter>{t17}{t19}</ConfirmModalButtonFooter>;
    $[60] = t17;
    $[61] = t19;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== remainingProps || $[64] !== t15 || $[65] !== t20 || $[66] !== t8) {
    t21 = <ConfirmModal {...remainingProps}>{t8}{t15}{t20}</ConfirmModal>;
    $[63] = remainingProps;
    $[64] = t15;
    $[65] = t20;
    $[66] = t8;
    $[67] = t21;
  } else {
    t21 = $[67];
  }
  return t21;
});
function _temp(message) {
  return <MessageListBoxListItem key={message}>{message}</MessageListBoxListItem>;
}
function _temp2(message_0) {
  return <MessageListBoxListItem key={message_0}>{message_0}</MessageListBoxListItem>;
}